import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UngradedAnswersGetDto } from '@app/infrastructure/models/ungraded-answers/ungraded-answers-get.dto';
import {
  SkillsRubric,
  TOTAL_NOTE_POINTS,
} from '@app/infrastructure/utils/constants';
import {
  ScoringRubric,
  RubricScores,
} from '@app/infrastructure/utils/constants';

@Component({
  selector: `app-unit-grading-table`,
  template: `<app-unit-grading-table-presentation
    [dataSource]="dataSource"
    [displayedColumns]="displayedColumns"
    [columnIdList]="columnIdList"
    [skills]="skills"
    [scoringRubric]="scoringRubric"
    [skillsLength]="skillsLength"
    [scoreGradingPeriod]="scoreGradingPeriod"
    [gradingPeriod]="gradingPeriod"
  ></app-unit-grading-table-presentation>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitGradingListSmartComponent implements OnInit {
  @Input() public gradingPeriod: any;
  public scoreGradingPeriod: boolean;
  public skills = SkillsRubric;
  public scoringRubric = ScoringRubric;
  public rubricScores = [];
  public skillsLength: string[] = [];
  public dataSource = [];
  public displayedColumns;
  public averageRubric = 0;
  public columnIdList: string[];
  teacherId: string;
  classroomId: string;

  private ungradedAnswers: UngradedAnswersGetDto[] = [];

  constructor(private activatedRoute: ActivatedRoute) {}
  ngOnInit(): void {
    this.ungradedAnswers = this.activatedRoute.snapshot.data?.ungradedAnswers;
    this.scoreGradingPeriod = this.gradingPeriod.gradingType === 'SCORE';
    this.skillsLength = Object.keys(this.skills);

    this.gradingPeriod?.students?.forEach((student) => {
      const dataRow: any = { name: student.studentName };
      const unitTotals = [];

      this.gradingPeriod.units.forEach((unit) => {
        const propertyName = `unit-${unit.id}`;
        if (this.scoreGradingPeriod) {
          const { sum, totalUnitNotePoints, total } = this.getUnitScore(
            student,
            unit,
            this.gradingPeriod?.assignments,
          );
          unitTotals.push(total);
          dataRow[propertyName] = `${sum + totalUnitNotePoints}/${total}`;
        } else dataRow[propertyName] = this.getRubricScore(student, unit);
      });

      const overallPropertyName = 'overall';
      if (this.scoreGradingPeriod) {
        const { sum, totalNotePoints } = this.calculateOverallScore(student);
        const total = unitTotals.reduce(
          (previousValue, currentValue) => previousValue + currentValue,
          0,
        );

        dataRow[overallPropertyName] = `${sum + totalNotePoints}/${total}, ${
          (((sum + totalNotePoints) / total) * 100)?.toFixed(1) || 0
        }%`;
      } else {
        dataRow[overallPropertyName] = this.getOverallRubricScore(
          student,
          this.gradingPeriod.units,
        );
      }
      this.dataSource.push(dataRow);
    });

    this.displayedColumns = this.scoreGradingPeriod
      ? [
          {
            columnIndex: 0,
            columnId: 'name',
            columnName: 'Student Name',
          },
          {
            columnIndex: 1,
            columnId: 'overall',
            columnName: 'Overall Performance',
          },
        ]
      : [
          {
            columnIndex: 0,
            columnId: 'name',
            columnName: 'Student Name',
          },
          {
            columnIndex: 1,
            columnId: 'skills',
            columnName: 'Demonstrated Skills',
          },
          {
            columnIndex: 2,
            columnId: 'overall',
            columnName: 'Overall Performance',
          },
        ];

    this.gradingPeriod?.units.forEach((element, index) => {
      this.displayedColumns.push({
        columnIndex: index + 2,
        columnId: `unit-${element.id}`,
        columnName: `Grade ${element.unitGradeLevel.slice(0, 1)} - ${
          element.unitTitle
        }`,
        url: element.unitCoverImageUrl,
        highlight: this.highlightUnits(element),
        label: `Unit ${element.unitNumber} image`,
      });
    });

    this.columnIdList = this.displayedColumns.map((column) => column.columnId);
  }

  private calculateOverallScore(student: any): any {
    let totalUnitNotePoints = 0;
    const assignments = student.assignments.filter(
      (studentAssignment) => studentAssignment.completedAt,
    );
    const sum = assignments
      .map((studentAssignment) => studentAssignment.finalScore)
      .reduce((previousValue, currentValue) => previousValue + currentValue, 0);

    const groupedStudentAssignments = student.assignments.reduce(
      (previousValue, currentValue) => {
        if (
          !previousValue[
            currentValue?.classroomAssignment?.gradeUnitDay?.unitId
          ]
        ) {
          previousValue[
            currentValue?.classroomAssignment?.gradeUnitDay?.unitId
          ] = [];
        }
        previousValue[
          currentValue?.classroomAssignment?.gradeUnitDay?.unitId
        ].push(currentValue);
        return previousValue;
      },
      {},
    );

    for (const unit in groupedStudentAssignments) {
      if (groupedStudentAssignments.hasOwnProperty(unit)) {
        const notePoints = groupedStudentAssignments[unit]
          .map((studentAssignment) => {
            return studentAssignment?.notes?.map((note) => {
              return note?.score?.finalScore || 0;
            });
          })
          .filter((item) => item?.length);

        const merged = [].concat.apply([], notePoints);
        const sumOfNotes = merged.reduce(
          (previousValue, currentValue) => previousValue + currentValue,
          0,
        );
        totalUnitNotePoints +=
          sumOfNotes > TOTAL_NOTE_POINTS ? TOTAL_NOTE_POINTS : sumOfNotes;
      }
    }

    totalUnitNotePoints =
      totalUnitNotePoints >=
      TOTAL_NOTE_POINTS * groupedStudentAssignments.length
        ? TOTAL_NOTE_POINTS * groupedStudentAssignments.length
        : totalUnitNotePoints;

    return { sum, totalNotePoints: totalUnitNotePoints };
  }

  private getUnitScore(student: any, unit: any, assignmentList): any {
    const assignments = student.assignments.filter((studentAssignment) => {
      return (
        studentAssignment?.classroomAssignment?.gradeUnitDay?.unitId ===
          unit.id && studentAssignment?.completedAt
      );
    });
    const allAssignmentInUnit = assignmentList?.filter((assignment) => {
      return assignment?.gradeUnitDay?.unitId === unit.id;
    });

    const sum = assignments
      .map((studentAssignment) => studentAssignment.finalScore)
      .reduce((previousValue, currentValue) => previousValue + currentValue, 0);

    let totalUnitNotePoints = student.assignments
      .filter((sa) => sa?.classroomAssignment?.gradeUnitDay?.unitId === unit.id)
      .map((studentAssignment) => {
        return studentAssignment?.notes.map((note) => {
          const score = note?.score?.finalScore;
          return score ? score : 0;
        });
      })
      .flat()
      .reduce((previousValue, currentValue) => previousValue + currentValue, 0);

    let total = allAssignmentInUnit
      ?.map((assignment) => assignment?.baseScore)
      ?.reduce(
        (previousValue, currentValue) => previousValue + currentValue,
        0,
      );

    totalUnitNotePoints =
      totalUnitNotePoints >= TOTAL_NOTE_POINTS
        ? TOTAL_NOTE_POINTS
        : totalUnitNotePoints;
    total = total !== 0 ? total + TOTAL_NOTE_POINTS : total;
    return { sum, totalUnitNotePoints, total };
  }

  private getRubricScore(student: any, unit: any): any {
    const studentPerformance = this.gradingPeriod?.rubricPerformances.filter(
      (rp) => rp.studentId === student.id && rp.gradeUnitId === unit.id,
    );

    return {
      scores: studentPerformance,
    };
  }

  private getOverallRubricScore(student: any, units: any): any {
    let discussions = 0;
    let understanding = 0;
    let writing = 0;
    let listening = 0;
    let activities = 0;

    const rubricSkills: Array<any> = this.gradingPeriod.rubricSkills;
    const rubricPerformances: Array<any> =
      this.gradingPeriod.rubricPerformances;
    const studentPerformances: Array<any> = rubricPerformances.filter(
      (performance) => performance.studentId === student.id,
    );
    const overallScores = [];
    rubricSkills.forEach((skill) => {
      const skillPerformances = studentPerformances.filter(
        (performance) => performance.rubricSkillId === skill.id,
      );
      const average = Math.floor(
        skillPerformances
          .map(
            (performance) =>
              skill.possibleScores.find(
                (score) => score.value === performance.score,
              ).position,
          )
          .reduce((partialSum, value) => partialSum + value, 0) /
          skillPerformances.length,
      );
      if (!isNaN(average) && average >= 0) {
        const averagedScore = skill.possibleScores.find(
          (score) => score.position === average,
        );
        overallScores.push(averagedScore?.value || 'N/A');
      } else overallScores.push('N/A');
    });

    return {
      scores: overallScores,
    };
  }

  private highlightUnits(element) {
    return this.ungradedAnswers.some(
      (ungradedAnswer) => ungradedAnswer.gradeUnitId === element.id,
    );
  }
}
